import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useMatch } from "react-router-dom";
import { Button } from "reactstrap";
import { Images } from "@workspace/common";
import { fetchCandidate } from "@workspace/candidate/src/store/candidate/action";
import {
  CandidateDetailsSection,
  CandidateEducationDetailsSection,
  CandidateWorkExperienceDetailsSection,
  CandidateProjectDetailsSection,
  CandidateSkillsDetailsSection,
  CandidateCertificationDetailsSection,
  CandidateDetailsFooter,
} from "../../components";
import "./CandidateProfileEditor.scss";
import axios from "axios";
import { CANDIDATE_URL } from "@workspace/common/src/config";
import { BASE_CANDIDATES } from "@workspace/candidate/src/helpers/url_helper";

function CandidateProfileEditor({ selectedCandidateId, publicView }) {
  const dispatch = useDispatch();
  const navRef = useRef(null);
  const logoRef = useRef(null);
  const navigate = useNavigate();
  const match = useMatch("/candidate/:candidateId/edit");

  const { candidateId } = useParams();
  const [id, setId] = useState("");
  const [candidate, setCandidate] = useState({});
  const [isLogoHidden, setIsLogoHidden] = useState(false);

  useEffect(() => {
    const checkOverlap = () => {
      if (logoRef.current && navRef.current) {
        const logoRect = logoRef.current.getBoundingClientRect();
        const navRect = navRef.current.getBoundingClientRect();

        const isOverlapping =
          logoRect.right > navRect.left && logoRect.left < navRect.right;

        setIsLogoHidden(isOverlapping);
      }
    };

    checkOverlap();
    window.addEventListener("resize", checkOverlap);

    return () => {
      window.removeEventListener("resize", checkOverlap);
    };
  }, []);

  // Check for SelectedCandidateId
  useEffect(() => {
    if (selectedCandidateId) {
      setId(selectedCandidateId);
    } else {
      setId(candidateId);
    }
  }, [candidateId, selectedCandidateId]);

  const candidateData = useSelector(
    (state) => state?.CandidateReducer?.candidate
  );

  useEffect(() => {
    if (id) {
      if (publicView) {
        axios
          .get(`${CANDIDATE_URL}${BASE_CANDIDATES}/public/id/${id}`)
          .then((response) => {
            setCandidate(response.data);
          });
      } else {
        dispatch(fetchCandidate(id));
      }
    }
  }, [id]);

  useEffect(() => {
    if (!publicView && candidateData != null) {
      setCandidate(candidateData);
    }
  }, [candidateData, publicView]);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const offset = 70;
      const elementPosition =
        section.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <header className="p-3 header-portfolio sticky-top bg-white shadow-sm">
        <div className="container-fluid d-flex align-items-center justify-content-between position-relative">
          {/* Logo on the left */}
          <img
            ref={logoRef}
            src={Images.avensys_full_logo}
            alt="avensys-logo"
            width="150"
            className={`portfolio-logo ${isLogoHidden ? "d-none" : ""}`}
          />

          {/* Navigation centered */}
          <nav
            ref={navRef}
            className="d-flex flex-row gap-4 align-items-center position-absolute start-50 translate-middle-x"
          >
            {[
              { id: "introduction", label: "Introduction" },
              { id: "work-experience", label: "Work Experience" },
              { id: "projects", label: "Projects" },
              { id: "skills", label: "Skills" },
              { id: "education", label: "Education" },
              { id: "certifications", label: "Certifications" },
            ].map((item) => (
              <div
                key={item.id}
                className="header-portfolio-navitem text-nowrap"
                onClick={() => scrollToSection(item.id)}
                onKeyPress={(e) =>
                  e.key === "Enter" && scrollToSection(item.id)
                }
                role="button"
                tabIndex={0}
              >
                {item.label}
              </div>
            ))}
          </nav>
          {match && (
            <Button
              onClick={() => navigate("/logout")}
              className="candidate-logout"
            >
              Logout
            </Button>
          )}
        </div>
      </header>
      {/* Introduction */}
      <div
        id="introduction"
        className="d-flex justify-content-center px-3 py-5"
        style={{ backgroundColor: "#F2F4F7" }}
      >
        <div
          className="d-flex flex-row justify-content-between gap-2"
          style={{ width: "75%" }}
        >
          <CandidateDetailsSection
            id={id}
            candidate={candidate}
            publicView={publicView}
          />
        </div>
      </div>
      {/* Work Experience */}
      <div
        id="work-experience"
        className="d-flex justify-content-center p-3"
        style={{ backgroundColor: "#fff" }}
      >
        <div style={{ width: "75%" }}>
          <CandidateWorkExperienceDetailsSection
            id={id}
            publicView={publicView}
          />
        </div>
      </div>

      {/* Projects */}
      <div
        id="projects"
        className="d-flex justify-content-center p-3"
        style={{ backgroundColor: "#fff" }}
      >
        <div style={{ width: "75%" }}>
          <CandidateProjectDetailsSection id={id} publicView={publicView} />
        </div>
      </div>

      {/* Skills */}
      <div
        id="skills"
        className="d-flex justify-content-center p-3"
        style={{ backgroundColor: "#fff" }}
      >
        <div style={{ width: "75%" }}>
          <CandidateSkillsDetailsSection
            id={id}
            candidate={candidate}
            publicView={publicView}
          />
        </div>
      </div>

      {/* Education */}
      <div
        id="education"
        className="d-flex justify-content-center p-3"
        style={{ backgroundColor: "#fff" }}
      >
        <div style={{ width: "75%" }}>
          <CandidateEducationDetailsSection id={id} publicView={publicView} />
        </div>
      </div>

      {/* Certifications */}
      <div
        id="certifications"
        className="d-flex justify-content-center p-3"
        style={{ backgroundColor: "#fff" }}
      >
        <div style={{ width: "75%" }}>
          <CandidateCertificationDetailsSection
            id={id}
            publicView={publicView}
          />
        </div>
      </div>

      {/* Assessments */}
      {/* <div
        id="assessments"
        className="d-flex justify-content-center p-3"
        style={{ backgroundColor: "#fff" }}
      >
        <div style={{ width: "75%" }}>
          <CandidateAssessmentDetailsSection id={id} />
        </div>
      </div> */}

      {/* Footer */}
      <footer
        className="d-flex justify-content-center p-3"
        style={{ backgroundColor: "#ffffff", borderTop: "1px solid #E7E9EB" }}
      >
        <CandidateDetailsFooter candidate={candidate} />
      </footer>
    </>
  );
}

export default CandidateProfileEditor;
